import LoadingIndicator from 'src/components/LoadingIndicator';
import NavTab from 'src/components/NavTab';
import SearchBar from 'src/components/SearchBar';
import {
  ShipmentBanner,
  ShipmentCost,
  ShipmentUnits
} from 'src/components/ShipmentBanner';
import { StatusBadge } from 'src/components/StatusBadge';
import logisticsImg from 'src/images/logistics.svg';
import {
  Button,
  Card,
  Col,
  Container,
  Icon,
  InputSelect,
  Link,
  ListGroup,
  ListGroupItem,
  Nav,
  Row,
  ScreenSize,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  UncontrolledTooltip,
  useApp,
  useUrlState
} from 'straightline-ui';
import { STATUS, STATUSES } from 'straightline-utils/constants';
import { formatDate } from 'straightline-utils/time';

const shipmentStatuses = [...STATUSES].filter(
  (status) => status.value !== STATUS.BOOKED
);
shipmentStatuses.sort((a, b) => (a.order > b.order ? 1 : -1));

function truncate(text) {
  if (!text) {
    return '';
  }

  if (text.length <= 30) {
    return text;
  }

  return text.substr(0, 30) + '\u2026';
}

const sortLabels = {
  pickup_date: 'Pickup Date',
  due_date: 'Delivery Date',
  status_type: 'Status'
};

export default function Shipments() {
  const app = useApp();

  const queryState = useUrlState();
  const query = queryState.getInState('query', { $sort: { pickup_date: -1 } });
  const status_type = queryState.getInState('query.status_type', 'all');

  const {
    result: shipments,
    isLoading,
    isError,
    isFetching
  } = app
    .service('api/shipments')
    .useFind({ query }, { keepPreviousData: true });

  const handleTabClick = async (status) => {
    const { ...newQuery } = queryState.getInState('query', {});

    if (status === 'all') {
      delete newQuery.status_type;
    } else {
      newQuery.status_type = status;
    }

    delete newQuery.$skip;
    delete newQuery.$limit;

    queryState.setInState('query', newQuery);
  };

  if (isLoading || isError) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (!queryState.getInState('query') && shipments.total === 0) {
    return <NoShipments />;
  }

  return (
    <Container fluid className="pb-5">
      <div className="header pt-md-2">
        <div className="header-body">
          <Row className="align-items-center">
            <Col>
              <h6 className="header-pretitle">Overview</h6>
              <h1 className="header-title">Booked Loads</h1>
            </Col>
            <Col className="col-auto">
              <Button to="/shipment-request" color="primary" className="mr-2">
                <Icon type="truck" className="mr-2" />
                Post Truck
              </Button>
              <Button to="/shipment-matches" color="primary">
                <Icon type="clipboard" className="mr-2" />
                Load Board
              </Button>
            </Col>
          </Row>
          <ScreenSize sm md lg xl>
            {() => (
              <Row className="align-items-center">
                <Col>
                  <Nav tabs className="nav-overflow header-tabs">
                    <NavTab
                      tab="all"
                      onClick={handleTabClick}
                      activeTab={status_type}
                    >
                      All Loads
                    </NavTab>
                    {shipmentStatuses.map((status) => (
                      <NavTab
                        key={status.value}
                        tab={status.value}
                        onClick={handleTabClick}
                        activeTab={status_type}
                      >
                        {status.label}
                      </NavTab>
                    ))}
                  </Nav>
                </Col>
              </Row>
            )}
          </ScreenSize>
          <ScreenSize xs>
            {() => (
              <div className="mt-4">
                <InputSelect
                  value={status_type}
                  onChange={({ target }) => handleTabClick(target.value)}
                  options={[{ label: 'All Loads', value: 'all' }].concat(
                    shipmentStatuses
                  )}
                />
              </div>
            )}
          </ScreenSize>
        </div>
      </div>
      <div className={isFetching ? 'is-fetching' : ''}>
        <ScreenSize sm md lg xl>
          {() => (
            <ShipmentTable
              shipments={shipments.data}
              queryState={queryState}
              total={shipments.total}
            />
          )}
        </ScreenSize>
        <ScreenSize xs>
          {() => <ShipmentCards shipments={shipments.data} />}
        </ScreenSize>
      </div>
    </Container>
  );
}

const NoShipments = () => {
  return (
    <Container fluid className="pb-5">
      <div className="header pt-md-2">
        <div className="header-body">
          <Row className="align-items-center">
            <Col>
              <h6 className="header-pretitle">Overview</h6>
              <h1 className="header-title">Booked Loads</h1>
            </Col>
            <Col className="col-auto">
              <Button to="/shipment-request" color="primary" className="mr-2">
                <Icon type="truck" className="mr-2" />
                Post Truck
              </Button>
              <Button to="/shipment-matches" color="primary">
                <Icon type="clipboard" className="mr-2" />
                Load Board
              </Button>
            </Col>
          </Row>
        </div>
      </div>
      <Row className="justify-content-center pt-6">
        <Col lg="9">
          <Row className="align-items-center">
            <Col lg="6" className="text-center mb-4 mb-lg-0">
              <img
                src={logisticsImg}
                alt="Get Started"
                className="img-fluid mb-4 mb-xl-0"
              />
            </Col>
            <Col lg="6">
              <h1>Find Loads</h1>
              <p className="mb-4 text-muted lead">
                You haven't booked any loads yet. Use the Load Board to search
                for loads near you or Post a Truck to be notified when loads
                meet your search criteria.
              </p>
              <Row className="mb-n3">
                <Col xs="6" className="mb-3 pr-0">
                  <Button to="/shipment-request" color="primary" block>
                    <Icon type="truck" className="mr-2" />
                    Post Truck
                  </Button>
                </Col>
                <Col xs="6" className="mb-3">
                  <Button to="/shipment-matches" color="primary" block>
                    <Icon type="clipboard" className="mr-2" />
                    Load Board
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

const ShipmentTable = ({ shipments, queryState, total }) => {
  return (
    <Card>
      <SearchBar
        queryState={queryState}
        total={total}
        sortLabels={sortLabels}
      />
      <div className="table-responsive">
        <Table className="table table-nowrap card-table table-hover">
          <TableHead>
            <TableRow>
              <TableHeader className="py-3">Status</TableHeader>
              <TableHeader className="py-3">Shipper</TableHeader>
              <TableHeader className="py-3">Pickup</TableHeader>
              <TableHeader className="py-3">Delivery</TableHeader>
              <TableHeader className="py-3">Service</TableHeader>
              <TableHeader className="py-3">Details</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody className="list">
            {shipments.map((shipment) => (
              <TableRow key={shipment.id} to={`/shipments/${shipment.id}`}>
                <TableData style={{ width: '1%' }}>
                  <StatusBadge status={shipment.status}>
                    #{shipment.id} |{' '}
                  </StatusBadge>
                </TableData>
                <TableData>
                  <div>{shipment.shipper.name}</div>
                  <div>
                    {shipment.reference_number ? (
                      truncate(shipment.reference_number)
                    ) : (
                      <span className="text-muted">Pickup # not available</span>
                    )}
                  </div>
                </TableData>
                <TableData className="" style={{ width: '1%' }}>
                  <div>
                    {`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}
                    {shipment.pickup_address.name && (
                      <span id={`pickup-name-${shipment.id}`} className="ml-1">
                        <Icon type="info" className="text-secondary" />
                        <UncontrolledTooltip
                          target={`pickup-name-${shipment.id}`}
                        >
                          {shipment.pickup_address.name}
                        </UncontrolledTooltip>
                      </span>
                    )}
                  </div>
                  <div>{formatDate(shipment.pickup_date, 'LLL do, yyyy')}</div>
                </TableData>
                <TableData
                  className=""
                  style={{ width: '1%', position: 'relative' }}
                >
                  <div style={{ position: 'absolute', left: -12, top: 21 }}>
                    <Icon
                      type="arrow-right text-success"
                      style={{ fontSize: 25 }}
                    />
                  </div>
                  <div>
                    {`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}
                    {shipment.delivery_address.name && (
                      <span
                        id={`delivery-name-${shipment.id}`}
                        className="ml-1"
                      >
                        <Icon type="info" className="text-secondary" />
                        <UncontrolledTooltip
                          target={`delivery-name-${shipment.id}`}
                        >
                          {shipment.delivery_address.name}
                        </UncontrolledTooltip>
                      </span>
                    )}
                  </div>
                  <div>{formatDate(shipment.due_date, 'LLL do, yyyy')}</div>
                </TableData>
                <TableData>
                  <div>{shipment.rate.service_level.name}</div>
                  <ShipmentCost shipment={shipment} />
                </TableData>
                <TableData>
                  <ShipmentUnits shipment={shipment} />
                </TableData>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

const ShipmentCards = ({ shipments }) => {
  return (
    <Card className="mx-n3">
      <ListGroup className="list-group-flush">
        {shipments.map((shipment) => {
          return (
            <ListGroupItem
              key={shipment.id}
              tag={Link}
              to={`/shipments/${shipment.id}`}
              className="list-group-item-action px-3"
            >
              <ShipmentBanner shipment={shipment} />
              <ListGroup className="list-group list-group-flush list-group-activity">
                <ListGroupItem className="py-3">
                  <Row>
                    <Col xs="auto">
                      <div className="avatar avatar-sm">
                        <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                          <Icon type="clipboard" />
                        </div>
                      </div>
                    </Col>
                    <Col className="ml-n2">
                      <div className="font-weight-bold">
                        {shipment.shipper.name}
                      </div>
                      <div className="mb-3 small text-primary mt-n1 font-weight-bold">
                        {shipment.rate.service_level.name}
                      </div>
                      <StatusBadge status={shipment.status}>
                        {shipment.id} |{' '}
                      </StatusBadge>
                      <div>
                        <span className="text-secondary">SL#: </span>
                        {shipment.id}
                      </div>
                      {shipment.reference_number && (
                        <div>
                          <span className="text-secondary">PICKUP#: </span>
                          {shipment.reference_number}
                        </div>
                      )}
                    </Col>
                  </Row>
                </ListGroupItem>
                <ListGroupItem className="py-3">
                  <Row>
                    <Col xs="auto">
                      <div className="avatar avatar-sm">
                        <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                          <Icon type="truck" />
                        </div>
                      </div>
                    </Col>
                    <Col className="ml-n2">
                      <div className="font-weight-bold">
                        {`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}
                      </div>
                      <div>
                        {formatDate(shipment.pickup_date, 'LLL d, yyyy')}
                      </div>
                    </Col>
                  </Row>
                </ListGroupItem>
                <ListGroupItem className="pt-3 pb-0">
                  <Row>
                    <Col xs="auto">
                      <div className="avatar avatar-sm">
                        <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                          <Icon type="map-pin" />
                        </div>
                      </div>
                    </Col>
                    <Col className="ml-n2">
                      <div className="font-weight-bold">
                        {`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}
                      </div>
                      <div>{formatDate(shipment.due_date, 'LLL d, yyyy')}</div>
                    </Col>
                  </Row>
                </ListGroupItem>
              </ListGroup>
            </ListGroupItem>
          );
        })}
      </ListGroup>
    </Card>
  );
};
